import { createEmptyResourcePool, createEmptyTurnActionUsage } from "../state/state-helpers";
import {
  LANE_IDS,
  PLAYER_IDS,
  type GameEvent,
  type GameState,
  type LaneState,
  type PlayerId,
  type PlayerLaneState,
} from "../types";
import type { TransitionResult } from "../transitions/advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

interface TemporaryModifierRemoval {
  readonly laneId: (typeof LANE_IDS)[number];
  readonly affectedPlayerId: PlayerId;
  readonly modifierIds: readonly string[];
}

/**
 * The current Core Battle state has no effect-duration model and no producer
 * for persistent entries in `combatModifiers`. It is therefore the narrowly
 * defined until-end-of-turn modifier bucket for this foundation. Printed
 * Commander/Army values and Land attack/guard bonuses do not use this field.
 */
function removeKnownTemporaryCombatModifiers(state: Readonly<GameState>): {
  readonly lanes: Readonly<Record<(typeof LANE_IDS)[number], LaneState>>;
  readonly removals: readonly TemporaryModifierRemoval[];
} {
  const removals: TemporaryModifierRemoval[] = [];
  let changed = false;
  const lanes = { ...state.lanes } as Record<(typeof LANE_IDS)[number], LaneState>;

  for (const laneId of LANE_IDS) {
    const lane = state.lanes[laneId];
    let players: Record<PlayerId, PlayerLaneState> | null = null;

    for (const affectedPlayerId of PLAYER_IDS) {
      const side = lane.players[affectedPlayerId];
      if (side.combatModifiers.length === 0) continue;

      if (players === null) players = { ...lane.players };
      players[affectedPlayerId] = { ...side, combatModifiers: [] };
      removals.push({
        laneId,
        affectedPlayerId,
        modifierIds: side.combatModifiers.map((modifier) => modifier.modifierId),
      });
      changed = true;
    }

    if (players !== null) lanes[laneId] = { ...lane, players };
  }

  return { lanes: changed ? lanes : state.lanes, removals };
}

/** Resolves the Core Battle Cleanup boundary before active-player handoff. */
export function resolveCleanupPhase(
  state: Readonly<GameState>,
  actionSequence: number,
  startingEventOrdinal: number,
): TransitionResult {
  const playerId = state.activePlayerId;
  const player = state.players[playerId];
  const discardedCardInstanceIds = [...player.hand];
  const clearedResources = { ...player.kingdomPool };
  const previousActionUsage = {
    ...player.actionsUsed,
    eventAuthorizationLanesUsed: [...player.actionsUsed.eventAuthorizationLanesUsed],
    attacksMadeFromLanes: [...player.actionsUsed.attacksMadeFromLanes],
  };
  const actionUsage = createEmptyTurnActionUsage();
  const resultingPool = createEmptyResourcePool();
  const modifiers = removeKnownTemporaryCombatModifiers(state);
  const events: GameEvent[] = [];
  const nextEventId = () => eventId(actionSequence, startingEventOrdinal + events.length);

  events.push({
    type: "cleanup-started",
    eventId: nextEventId(),
    actionSequence,
    playerId,
    turnNumber: state.turnNumber,
  });
  events.push({
    type: "cleanup-hand-discarded",
    eventId: nextEventId(),
    actionSequence,
    playerId,
    cardInstanceIds: discardedCardInstanceIds,
    handCountBefore: player.hand.length,
    handCountAfter: 0,
    discardCountBefore: player.discard.length,
    discardCountAfter: player.discard.length + discardedCardInstanceIds.length,
  });
  if (modifiers.removals.length > 0) {
    events.push({
      type: "temporary-combat-modifiers-cleared",
      eventId: nextEventId(),
      actionSequence,
      playerId,
      removals: modifiers.removals,
    });
  }
  events.push({
    type: "event-authorization-cleared",
    eventId: nextEventId(),
    actionSequence,
    playerId,
    eventsPlayedBefore: player.actionsUsed.eventsPlayed,
    eventAuthorizationLanesUsedBefore: [...player.actionsUsed.eventAuthorizationLanesUsed],
  });
  events.push({
    type: "kingdom-pool-cleared",
    eventId: nextEventId(),
    actionSequence,
    playerId,
    clearedResources,
    resultingPool,
  });
  events.push({
    type: "turn-action-usage-reset",
    eventId: nextEventId(),
    actionSequence,
    playerId,
    previousActionUsage,
    actionUsage,
  });

  let nextState: GameState = {
    ...state,
    lanes: modifiers.lanes,
    players: {
      ...state.players,
      [playerId]: {
        ...player,
        hand: [],
        discard: [...player.discard, ...discardedCardInstanceIds],
        kingdomPool: resultingPool,
        actionsUsed: actionUsage,
      },
    },
  };

  if (
    (state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5) &&
    (nextState.schemaVersion === 3 ||
      nextState.schemaVersion === 4 ||
      nextState.schemaVersion === 5)
  ) {
    events.push({
      type: "supply-status-reset",
      eventId: nextEventId(),
      actionSequence,
      playerId,
      previousSupplyStatus: state.supplyStatus,
      supplyStatus: { type: "not-started" },
    });
    nextState = { ...nextState, supplyStatus: { type: "not-started" } };
  }

  return { state: nextState, events };
}
